import React, { createContext, useContext, useEffect, useState } from 'react';
import { useUser } from './User';
import {
  createBadToolSerial,
  getBadToolSerials,
  removeBadToolSerial,
  updateBadToolSerial,
} from '../libs/db-lib';

export interface BadToolSerialsInterface {
  areBadToolSerialsLoading: boolean;
  badToolSerials: any[] | null;
  showDeleted: boolean;
  setShowDeleted: (show: boolean) => void;
  handleRefresh(overrideAreBadToolSerialsLoading: boolean): any;
  handleCreate(toolManufacturerUUID: string, toolSerial: string, message: string, toolModelUUID?: string): any;
  handleUpdate(badToolSerialUUID: string, toolManufacturerUUID: string, toolSerial: string, message: string, toolModelUUID?: string): any;
  handleRemove(badToolSerialUUID: string): any;
}

export const BadToolSerialsContext = createContext<BadToolSerialsInterface | undefined>(undefined);

export const useBadToolSerials = () => {
  const context = useContext(BadToolSerialsContext);

  if (!context) {
    throw new Error('useBadToolSerials must be used within BadToolSerialsProvider!');
  }

  return context;
};

type BadToolSerialsProviderType = {
  children: JSX.Element;
};

export function BadToolSerialsProvider({ children }: BadToolSerialsProviderType) {
  const { user } = useUser();

  const [areBadToolSerialsLoading, setAreBadToolSerialsLoading] = useState<boolean>(true);
  const [badToolSerials, setBadToolSerials] = useState<any[]>([]);
  const [showDeleted, setShowDeleted] = useState<boolean>(false);

  useEffect(() => {
    if (user && ['ADMIN', 'MANAGER'].includes(user.userType)) {
      handleRefresh(true);
      const refreshInterval = setInterval(() => {
        handleRefresh(false);
      }, 300000); // refreshing every 5 minutes
      return () => {
        clearInterval(refreshInterval);
      };
    } else {
      setBadToolSerials([]);
    }
  }, [user, showDeleted]);

  const handleRefresh = async (overrideAreBadToolSerialsLoading: boolean) => {
    if (!user || (user && !['ADMIN', 'MANAGER'].includes(user.userType))) {
      return;
    }

    if (overrideAreBadToolSerialsLoading) {
      setAreBadToolSerialsLoading(true);
    }

    const response = await getBadToolSerials(showDeleted);

    if (!response.error) {
      setBadToolSerials(response);
    }

    setAreBadToolSerialsLoading(false);
    return response;
  };

  const handleCreate = async (toolManufacturerUUID: string, toolSerial: string, message: string, toolModelUUID?: string) => {
    if (!user || (user && !['ADMIN', 'MANAGER'].includes(user.userType))) {
      return;
    }

    const response = await createBadToolSerial(toolManufacturerUUID, toolSerial, message, toolModelUUID);

    if (!response.error) {
      setBadToolSerials(prev => [...prev, response]);
    }

    return response;
  };

  const handleUpdate = async (
    badToolSerialUUID: string,
    toolManufacturerUUID: string,
    toolSerial: string,
    message: string,
    toolModelUUID?: string
  ) => {
    if (!user || (user && !['ADMIN', 'MANAGER'].includes(user.userType))) {
      return;
    }

    const response = await updateBadToolSerial(badToolSerialUUID, toolManufacturerUUID, toolSerial, message, toolModelUUID);

    if (!response.error) {
      setBadToolSerials(prev => prev.map((bts: any) =>
        bts.badToolSerialUUID === badToolSerialUUID
          ? { ...bts, toolManufacturerUUID, toolSerial, message, toolModelUUID: toolModelUUID || null }
          : bts
      ));
    }

    return response;
  };

  const handleRemove = async (badToolSerialUUID: string) => {
    if (!user || (user && !['ADMIN', 'MANAGER'].includes(user.userType))) {
      return;
    }

    const response = await removeBadToolSerial(badToolSerialUUID);

    if (!response.error) {
      if (showDeleted) {
        setBadToolSerials(prev => prev.map((bts: any) =>
          bts.badToolSerialUUID === badToolSerialUUID
            ? { ...bts, deleted: true, deletedBy: user.userID, deletedOn: new Date().toISOString() }
            : bts
        ));
      } else {
        setBadToolSerials(prev => prev.filter((bts: any) => bts.badToolSerialUUID !== badToolSerialUUID));
      }
    }

    return response;
  };

  const provider = {
    areBadToolSerialsLoading,
    badToolSerials,
    showDeleted,
    setShowDeleted,
    handleRefresh,
    handleCreate,
    handleUpdate,
    handleRemove,
  };

  return <BadToolSerialsContext.Provider value={provider}>{children}</BadToolSerialsContext.Provider>;
}